/**
 * Import agents from YAML (V1.1-EA-T07): a file (or pasted text) of documents, previewed with `/v1/apply`'s dry
 * run (what each would create or change), then applied. An agent that exists by name gets a new version; one
 * that does not is created. Nothing is published: that stays a choice in the builder.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useId, useState } from "react";
import { data, type Schemas } from "../api/client";
import { useBff, useMe } from "../app/session";
import { useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState } from "../ui/states";
import { Table } from "../ui/Table";

type Applied = Schemas["ApplyOut"];

export function ImportAgents({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const t = useT();
  const id = useId();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<Applied | null>(null);

  const apply = (dryRun: boolean) => data(api.POST("/v1/apply", {
    params: { query: { dry_run: dryRun } },
    body: text,
    bodySerializer: (body: unknown) => body as BodyInit,
    headers: { "Content-Type": "application/yaml" },
  })) as Promise<Applied>;
  const check = useMutation({ mutationFn: () => apply(true), onSuccess: setPreview });
  const run = useMutation({
    mutationFn: () => apply(false),
    onSuccess: async () => {
      await queries.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith("agents") && q.queryKey[1] === tenant });
      reset();
      onOpenChange(false);
    },
  });
  const reset = () => {
    setText("");
    setPreview(null);
    check.reset();
    run.reset();
  };
  const changed = (next: string) => {
    setText(next);
    setPreview(null); // a preview is of the text it was made from
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next) reset(); onOpenChange(next); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-12 max-h-[85vh] w-full max-w-2xl -translate-x-1/2 space-y-4 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-6 shadow-xl">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Dialog.Title className="text-lg font-semibold">{t("agents.import.title")}</Dialog.Title>
              <Dialog.Description className="text-sm text-[var(--color-muted)]">{t("agents.import.intro")}</Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <Button variant="secondary" aria-label={t("common.close")}><X size={16} aria-hidden="true" /></Button>
            </Dialog.Close>
          </div>
          <label className="flex flex-col gap-1 text-sm">
            {t("agents.import.file")}
            <input type="file" accept=".yaml,.yml,application/yaml,text/yaml"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (file) changed(await file.text());
              }} />
          </label>
          <label htmlFor={`${id}-text`} className="flex flex-col gap-1 text-sm">
            {t("agents.import.text")}
            <textarea id={`${id}-text`} rows={10} value={text} onChange={(e) => changed(e.target.value)} spellCheck={false}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 font-mono text-xs" />
          </label>
          {(check.error || run.error) && <ErrorState error={check.error ?? run.error} />}
          {preview && (
            <section aria-labelledby={`${id}-preview`} className="space-y-2">
              <h3 id={`${id}-preview`} className="font-semibold">{t("agents.import.preview", { changed: preview.changed })}</h3>
              <Table caption={t("agents.import.previewTable")} rows={preview.results} rowKey={(r) => `${r.kind}:${r.name}`}
                columns={[
                  { header: t("agents.import.kind"), cell: (r) => r.kind },
                  { header: t("agents.import.name"), cell: (r) => <span className="font-mono text-xs">{r.name}</span> },
                  { header: t("agents.import.action"), cell: (r) => t(`agents.import.action.${r.action}` as MessageId, { version: r.version ?? 0 }) },
                  { header: t("agents.import.changes"), cell: (r) => (r.action === "update" ? r.diff.length : "—") },
                ]} />
            </section>
          )}
          <div className="flex gap-2">
            <Button variant={preview ? "secondary" : "primary"} disabled={!text.trim() || check.isPending} onClick={() => check.mutate()}>
              {t("agents.import.check")}
            </Button>
            <Button disabled={!preview || preview.changed === 0 || run.isPending} onClick={() => run.mutate()}>
              {t("agents.import.apply", { changed: preview?.changed ?? 0 })}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
